import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Newsletter subscribe card. Pass a `source` (e.g. blog slug) so admin can
 * see which page drove each signup.
 * <Newsletter source="blog:renting-in-bengaluru" />
 */
export default function Newsletter({ source, compact = false }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, source });
      setDone(true);
      toast.success(data.already_subscribed ? "You're already on the list ✨" : "You're in — welcome to the list");
    } catch (err) {
      toast.error("Could not subscribe. Try again in a moment.");
    } finally { setBusy(false); }
  };

  if (compact) {
    return (
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-compact">
        <Input
          data-testid="newsletter-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={done}
          className="flex-1 h-11 bg-white"
        />
        <Button
          type="submit"
          data-testid="newsletter-submit"
          disabled={busy || done}
          className="tlr-gold-btn rounded-full h-11 px-6 whitespace-nowrap"
        >
          {done ? <><Check size={15} className="mr-1" /> Subscribed</> :
           busy ? <><Loader2 size={15} className="animate-spin mr-1" /> Joining…</> :
                  <>Get weekly guides</>}
        </Button>
      </form>
    );
  }

  return (
    <section
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FBF6EC] to-[#F4E8C6] border border-[#EFE3D0] p-8 md:p-12"
      data-testid="newsletter-cta"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#C9A24A]/25 blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[#7A1E2A]/10 blur-3xl" />
      <div className="relative max-w-2xl">
        <span className="inline-flex items-center gap-2 bg-[#7A1E2A]/10 border border-[#7A1E2A]/20 text-[#7A1E2A] px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-4">
          <Sparkles size={12} /> Free — no spam
        </span>
        <h3 className="font-display text-3xl md:text-4xl text-[#7A1E2A] leading-tight mb-3">
          Get honest renting tips in your inbox.
        </h3>
        <p className="text-gray-700 leading-relaxed mb-6">
          One evergreen guide per week — rent agreement checklists, city renting playbooks, negotiation scripts. Zero fluff. Unsubscribe any time.
        </p>
        {done ? (
          <div className="inline-flex items-center gap-2 bg-white border border-[#7A1E2A]/20 text-[#7A1E2A] px-5 py-3 rounded-full text-sm font-semibold" data-testid="newsletter-done">
            <Check size={16} className="text-emerald-600" /> You're on the list — see you Monday.
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-lg">
            <div className="relative flex-1">
              <Mail size={16} className="absolute top-1/2 -translate-y-1/2 left-4 text-gray-400" />
              <Input
                data-testid="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="pl-11 h-12 bg-white text-base"
              />
            </div>
            <Button
              type="submit"
              data-testid="newsletter-submit"
              disabled={busy}
              className="tlr-gold-btn rounded-full h-12 px-6 whitespace-nowrap"
            >
              {busy ? <><Loader2 size={16} className="animate-spin mr-2" /> Joining…</> : "Get weekly guides"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
